import React, { useEffect, useRef, useState } from "react";
import { toPng, toBlob } from "html-to-image";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { useAuth } from "@/lib/AuthContext";
import {
  Trophy, Crown, Shield, Target, Crosshair, Zap, Brain, Swords, CalendarCheck,
  Flame, TrendingUp, Heart, HeartCrack, Wand2, Sparkles, ThumbsDown, Coins,
  Medal, TrendingDown, Infinity as Inf, AlarmClock, Clock, Share2, Download, X, Award, Undo2,
} from "lucide-react";

const ICONS = {
  overall_champion: Crown, prediction_winner: Target, survivor_winner: Shield, bracket_winner: Trophy,
  oracle: Crosshair, lucky_predictor: Sparkles, sniper: Target, maximiser: Zap, big_brain: Brain,
  clutch_performer: Swords, matchday_king: Crown, hot_streak: Flame, comeback_kid: TrendingUp,
  loyalist: Heart, heart_over_head: Heart, delusional: HeartCrack, grandmaster_jinx: Wand2, reverse_jinx: Wand2,
  hall_of_shame: ThumbsDown, coin_toss: Coins, nearly_there: Medal, bridesmaid: Medal,
  freefall: TrendingDown, marathon_man: Inf, early_bird: AlarmClock, last_minute: Clock,
};
const SITE_URL = "https://jinxingsports.com";
const iconFor = (k) => ICONS[k] || Award;

const Avatar = ({ w }) => (w ? <img src={w.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> :
  <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center"><Trophy size={14} className="text-zinc-600" /></div>);

const AwardCard = ({ a, onShare }) => {
  const Icon = iconFor(a.key);
  const w = a.winner;
  return (
    <div className="border border-white/10 bg-black/30 p-4 flex flex-col gap-2 group hover:border-white/25 transition-colors" data-testid={`award-${a.key}`}>
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 bg-primary-accent/15 border border-primary-accent/40 flex items-center justify-center flex-shrink-0"><Icon size={18} className="text-primary-accent" /></div>
        {w && <button onClick={() => onShare(a)} data-testid={`share-award-${a.key}`} className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-primary-accent"><Share2 size={14} /></button>}
      </div>
      <div className="font-display text-lg uppercase leading-tight">{a.name}</div>
      {w ? (
        <>
          <div className="flex items-center gap-2 mt-0.5">
            {w.picture ? <img src={w.picture} alt="" className="w-6 h-6 rounded-full object-cover" /> : null}
            <span className="font-display tracking-wider truncate">{w.name}</span>
          </div>
          <div className="text-primary-accent text-sm font-display">{w.stat}</div>
        </>
      ) : <div className="text-zinc-600 text-sm uppercase tracking-widest mt-1">Not awarded</div>}
      <div className="text-[11px] text-zinc-500 italic mt-auto pt-1">{a.description}</div>
    </div>
  );
};

const HeroCard = ({ a, onShare }) => {
  const Icon = iconFor(a.key);
  const w = a.winner;
  const isOverall = a.key === "overall_champion";
  return (
    <div className={`relative p-5 border ${isOverall ? "border-yellow-500/60 bg-gradient-to-br from-yellow-500/15 via-yellow-500/5 to-transparent md:col-span-2" : "border-white/10 bg-black/30"}`} data-testid={`hero-${a.key}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="label-tag text-zinc-400">{a.name}</div>
        {w && <button onClick={() => onShare(a)} data-testid={`share-award-${a.key}`} className="text-zinc-400 hover:text-primary-accent"><Share2 size={16} /></button>}
      </div>
      {w ? (
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 flex items-center justify-center flex-shrink-0 ${isOverall ? "bg-yellow-500/20 border border-yellow-500/50" : "bg-primary-accent/15 border border-primary-accent/40"}`}>
            <Icon size={26} className={isOverall ? "text-yellow-400" : "text-primary-accent"} />
          </div>
          <div className="min-w-0">
            <div className={`font-display uppercase truncate ${isOverall ? "text-3xl" : "text-2xl"}`}>{w.name}</div>
            <div className="text-primary-accent font-display">{w.stat}</div>
            {isOverall && w.extra && (
              <div className="text-xs text-zinc-400 mt-1">{w.extra.exact} exact{w.extra.favourite_team ? ` · ${w.extra.favourite_team}` : ""}</div>
            )}
          </div>
        </div>
      ) : <div className="text-zinc-600 uppercase tracking-widest text-sm py-4">Not awarded yet</div>}
    </div>
  );
};

const Section = ({ title, items, hero, onShare, testid }) => (
  <div className="mb-10" data-testid={testid}>
    <h2 className="font-display text-2xl uppercase mb-4 flex items-center gap-2"><Trophy size={18} className="text-primary-accent" /> {title}</h2>
    <div className={`grid gap-3 ${hero ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"}`}>
      {items.map((a) => hero ? <HeroCard key={a.key} a={a} onShare={onShare} /> : <AwardCard key={a.key} a={a} onShare={onShare} />)}
    </div>
  </div>
);

export default function RollOfHonour() {
  const { active, refresh } = useTournament();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState(null);
  const [recapOpen, setRecapOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const cardRef = useRef(null);
  const recapRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    api.get(`/tournaments/${active.tournament_id}/roll-of-honour`)
      .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [active?.tournament_id]);

  const downloadNode = async (node, filename) => {
    if (!node || busy) return;
    setBusy(true);
    try {
      const url = await toPng(node, { pixelRatio: 2, cacheBust: true, skipFonts: true, backgroundColor: "#09090b" });
      const link = document.createElement("a");
      link.download = filename;
      link.href = url; link.click();
      toast.success("Image downloaded!");
    } catch { toast.error("Couldn't generate image."); }
    finally { setBusy(false); }
  };

  // Native Web Share (mobile) with a graceful download fallback (desktop / unsupported).
  const canWebShareFiles = typeof navigator !== "undefined" && !!navigator.canShare &&
    (() => { try { return navigator.canShare({ files: [new File([""], "x.png", { type: "image/png" })] }); } catch { return false; } })();

  const shareNode = async (node, filename, title) => {
    if (!node || busy) return;
    setBusy(true);
    try {
      const blob = await toBlob(node, { pixelRatio: 2, cacheBust: true, skipFonts: true, backgroundColor: "#09090b" });
      if (!blob) throw new Error("no blob");
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text: `${title} — jinxingsports.com` });
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = filename; link.href = url; link.click();
        URL.revokeObjectURL(url);
        toast.success("Image downloaded!");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share image.");
    } finally { setBusy(false); }
  };

  const download = () => downloadNode(cardRef.current, `jinxing-${share.key}.png`);
  const downloadRecap = () => downloadNode(recapRef.current, `jinxing-roll-of-honour.png`);
  const shareAward = () => shareNode(cardRef.current, `jinxing-${share.key}.png`, `${share.name} — ${data.name}`);
  const shareRecap = () => shareNode(recapRef.current, `jinxing-roll-of-honour.png`, `${data.name} — Roll of Honour`);

  const reactivate = async () => {
    if (reactivating) return;
    setReactivating(true);
    try {
      await api.post(`/admin/tournaments/${active.tournament_id}/reactivate`);
      toast.success("Tournament reactivated");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reactivate tournament");
    } finally {
      setReactivating(false);
    }
  };

  const isAdmin = user?.role === "admin";

  if (!active) return null;
  if (loading) return <div className="p-12 text-center text-zinc-500" data-testid="roh-loading">Loading Roll of Honour…</div>;
  if (!data) return <div className="p-12 text-center text-zinc-500">Roll of Honour unavailable.</div>;

  const ShareIcon = share ? iconFor(share.key) : Trophy;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="roll-of-honour-page">
      {isAdmin && data.status === "archived" && (
        <div className="flex justify-end mb-4">
          <button
            data-testid="reactivate-tournament-btn"
            onClick={reactivate}
            disabled={reactivating}
            className="inline-flex items-center gap-2 border border-primary-accent/60 text-primary-accent px-4 py-2 font-display text-xs uppercase tracking-widest hover:bg-primary-accent hover:text-white transition-colors disabled:opacity-50"
          >
            <Undo2 size={14} /> {reactivating ? "Reactivating…" : "Reactivate Tournament"}
          </button>
        </div>
      )}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 label-tag text-yellow-400 mb-2"><Crown size={14} /> {data.status === "archived" ? "Final" : "Provisional"} Roll of Honour</div>
        <h1 className="font-display text-4xl md:text-6xl uppercase">{data.name}</h1>
        <p className="text-zinc-400 text-sm mt-2">Champions, performances & pure banter — the tournament in trophies.</p>
        <button
          data-testid="share-roll-of-honour-btn"
          onClick={() => setRecapOpen(true)}
          className="inline-flex items-center gap-2 mt-5 bg-primary-accent text-white px-5 py-2.5 font-display text-sm uppercase tracking-widest hover:bg-[var(--primary-hover)] transition-colors"
        >
          <Share2 size={15} /> Share Roll of Honour
        </button>
      </div>

      <Section title="Champions" items={data.awards.champions} hero onShare={setShare} testid="roh-champions" />
      <Section title="Performance Awards" items={data.awards.performance} onShare={setShare} testid="roh-performance" />
      <Section title="Favourite Team Awards" items={data.awards.favourite_team} onShare={setShare} testid="roh-favteam" />
      <Section title="Banter & Jinx Awards" items={data.awards.banter} onShare={setShare} testid="roh-banter" />

      {share && (
        <div className="fixed inset-0 z-[210] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="award-share-modal"
          onClick={() => setShare(null)}>
          <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="label-tag text-zinc-400">Share Award</div>
              <button data-testid="award-share-close" onClick={() => setShare(null)} className="w-8 h-8 flex items-center justify-center border border-zinc-800 text-zinc-400 hover:border-white/30"><X size={14} /></button>
            </div>
            <div ref={cardRef} className="bg-zinc-950 grid-bg p-8 border border-white/10" data-testid="award-share-card">
              <div className="flex items-center gap-2.5 mb-8">
                <div className="w-8 h-8 bg-primary-accent flex items-center justify-center"><Trophy size={18} /></div>
                <span className="font-display text-lg tracking-wider">JINXING SPORTS</span>
              </div>
              <div className="w-16 h-16 bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center mb-4"><ShareIcon size={32} className="text-yellow-400" /></div>
              <div className="font-display text-4xl uppercase leading-none mb-3">{share.name}</div>
              <div className="font-display text-2xl uppercase text-primary-accent">{share.winner.name}</div>
              <div className="text-zinc-300 text-lg mt-1">{share.winner.stat}</div>
              <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  {data.name}<br />jinxingsports.com
                </div>
                <div className="bg-white p-1.5 rounded-sm flex-shrink-0">
                  <QRCodeSVG value={SITE_URL} size={52} level="M" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              {canWebShareFiles && (
                <button onClick={shareAward} disabled={busy} data-testid="award-share-native"
                  className="flex-1 bg-primary-accent text-white py-3 font-display tracking-widest text-sm uppercase hover:bg-[var(--primary-hover)] disabled:opacity-50 flex items-center justify-center gap-2">
                  <Share2 size={14} /> {busy ? "…" : "Share…"}
                </button>
              )}
              <button onClick={download} disabled={busy} data-testid="award-share-download"
                className={`${canWebShareFiles ? "flex-1 border border-primary-accent/60 text-primary-accent hover:bg-primary-accent/10" : "w-full bg-primary-accent text-white hover:bg-[var(--primary-hover)]"} py-3 font-display tracking-widest text-sm uppercase disabled:opacity-50 flex items-center justify-center gap-2`}>
                <Download size={14} /> {busy ? "Generating…" : "Download"}
              </button>
            </div>
          </div>
        </div>
      )}

      {recapOpen && (() => {
        const champs = data.awards.champions || [];
        const cById = Object.fromEntries(champs.map((a) => [a.key, a]));
        const overall = cById.overall_champion;
        const otherChamps = ["prediction_winner", "survivor_winner", "bracket_winner"]
          .map((k) => cById[k]).filter((a) => a && a.winner);
        const highlights = [...(data.awards.performance || []), ...(data.awards.banter || [])]
          .filter((a) => a.winner).slice(0, 4);
        return (
          <div className="fixed inset-0 z-[210] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="recap-share-modal"
            onClick={() => setRecapOpen(false)}>
            <div className="max-w-md w-full my-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div className="label-tag text-zinc-400">Share Roll of Honour</div>
                <button data-testid="recap-share-close" onClick={() => setRecapOpen(false)} className="w-8 h-8 flex items-center justify-center border border-zinc-800 text-zinc-400 hover:border-white/30"><X size={14} /></button>
              </div>
              <div ref={recapRef} className="bg-zinc-950 grid-bg p-7 border border-white/10" data-testid="recap-share-card">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-8 h-8 bg-primary-accent flex items-center justify-center"><Trophy size={18} /></div>
                  <span className="font-display text-lg tracking-wider">JINXING SPORTS</span>
                </div>
                <div className="label-tag text-yellow-400 flex items-center gap-1.5 mb-1"><Crown size={12} /> Roll of Honour</div>
                <div className="font-display text-3xl uppercase leading-none mb-5">{data.name}</div>

                {overall?.winner && (
                  <div className="border border-yellow-500/50 bg-gradient-to-br from-yellow-500/15 to-transparent p-4 mb-4">
                    <div className="label-tag text-yellow-300/80 mb-1">Overall Champion</div>
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center flex-shrink-0">
                        <Crown size={22} className="text-yellow-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display text-2xl uppercase leading-none truncate">{overall.winner.name}</div>
                        <div className="text-primary-accent font-display text-sm mt-0.5">{overall.winner.stat}</div>
                      </div>
                    </div>
                  </div>
                )}

                {otherChamps.length > 0 && (
                  <div className="grid grid-cols-1 gap-1.5 mb-4">
                    {otherChamps.map((a) => {
                      const Icon = iconFor(a.key);
                      return (
                        <div key={a.key} className="flex items-center gap-2.5 border border-white/10 bg-black/30 px-3 py-2">
                          <Icon size={16} className="text-primary-accent flex-shrink-0" />
                          <span className="text-[11px] uppercase tracking-widest text-zinc-400 w-28 flex-shrink-0">{a.name}</span>
                          <span className="font-display uppercase truncate">{a.winner.name}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {highlights.length > 0 && (
                  <div className="mb-5">
                    <div className="label-tag text-zinc-500 mb-2">Standout Performances</div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {highlights.map((a) => {
                        const Icon = iconFor(a.key);
                        return (
                          <div key={a.key} className="border border-white/10 bg-black/30 px-2.5 py-2">
                            <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-zinc-500 mb-0.5">
                              <Icon size={11} className="text-primary-accent" /> {a.name}
                            </div>
                            <div className="font-display text-sm uppercase truncate leading-tight">{a.winner.name}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    Play the next one<br />jinxingsports.com
                  </div>
                  <div className="bg-white p-1.5 rounded-sm flex-shrink-0">
                    <QRCodeSVG value={SITE_URL} size={56} level="M" />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                {canWebShareFiles && (
                  <button onClick={shareRecap} disabled={busy} data-testid="recap-share-native"
                    className="flex-1 bg-primary-accent text-white py-3 font-display tracking-widest text-sm uppercase hover:bg-[var(--primary-hover)] disabled:opacity-50 flex items-center justify-center gap-2">
                    <Share2 size={14} /> {busy ? "…" : "Share…"}
                  </button>
                )}
                <button onClick={downloadRecap} disabled={busy} data-testid="recap-share-download"
                  className={`${canWebShareFiles ? "flex-1 border border-primary-accent/60 text-primary-accent hover:bg-primary-accent/10" : "w-full bg-primary-accent text-white hover:bg-[var(--primary-hover)]"} py-3 font-display tracking-widest text-sm uppercase disabled:opacity-50 flex items-center justify-center gap-2`}>
                  <Download size={14} /> {busy ? "Generating…" : "Download"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
